// OrdersList.jsx — proc-v3 (F2.3e): QUINTA VISTA DEL MONOLITE in React,
// lista "Ordini", flag-gated (shell_proc_v3_orders, default OFF).
//
// Replica 1:1 renderOrders di proc-v0.js:11002-11055: stesso titolo/sottotitolo
// (hardcoded nel v1, NON dal dict), stesso bottone "+ Nuovo ordine", stesse
// colonne (# Ordine come link, Fornitore, Righe, Totale, Stato badge, Creato,
// Atteso), stesso ordinamento di default (Creato desc — parità byte-per-byte
// via rank v1), stesse azioni bulk (Segna confermati / Elimina — stesse
// primitive confirmWrite del monolite ⇒ payload identici, toast VERBATIM),
// stesso click riga → dettaglio V1 (order-detail).
//
// FONTE DEI TOTALI (mandato F2.3e): api.readOrders delega a
// win.store.orderTotal sugli ordini che l'API consegna (orderOut legge le
// FIGLIE normalizzate, F1.3 FASE C) — il v3 NON ricalcola per conto suo.
//
// QUIRK v1 CONSERVATI (parità bug-per-bug, S4 f23d):
//  - il link del codice PO (href="#" data-open) nel v1 NON ha handler e il
//    click riga IGNORA i controlli interattivi ⇒ il link è "morto" anche qui;
//  - annullare il confirm di "Elimina" ritorna undefined (≠ false) ⇒ la
//    selezione si azzera comunque, come nel makeDataTable v1.
//
// STATO: selezione a livello di PAGINA (factory) con pruning sugli id morti.
// DEVIAZIONE DICHIARATA (stessa dev.1 f23d dei Prodotti): il v1 ricrea la
// tabella (e azzera la selezione) a OGNI renderOrders — qui sopravvive ai
// refresh dati e si azzera a bulk conclusa o smontando la vista.
//
// CONFINI (pattern f23c): dettaglio ordine e modale nuovo ordine = V1
// (deleghe in api.js); MAI 'nav:badge' da questo modulo.

import React from 'react';
import { DataTable } from '../ui/DataTable.jsx';
import {
  tOrd, ORD_TEXTS, readOrders, fmtCreated,
  ordStatusBadgeClass, ordStatusLabel, ordStatusFilterText,
  ordTotalFilterText, ordCreatedFilterText, ordExpectedFilterText,
  openNewOrder, openOrderDetail, resolveOrderSelection, buildOrderBulkActions,
} from './api.js';

export function OrdersList({ win, tick, selection }) {
  const [, force] = React.useReducer((n) => n + 1, 0);

  // dati: letti dallo store del monolite a ogni refresh (tick dal wrapper) —
  // stessa composizione del v1 (api.readOrders, totali = store.orderTotal)
  const data = React.useMemo(() => readOrders(win), [win, tick]);

  // selezione: PRUNA gli id che non esistono più nello store (pattern f23d)
  React.useEffect(() => {
    let changed = false;
    for (const id of Array.from(selection)) {
      if (!win.store.getOrder(id)) { selection.delete(id); changed = true; }
    }
    if (changed) force();
  }, [data, selection, win]);

  const t = (k) => tOrd(win, k);

  const columns = [
    {
      key: 'code',
      label: t('col.po_number'),
      sortable: true,
      sortValue: (r) => r._v1Rank.code, // rank v1 ⇒ ordine byte-per-byte anche al click
      render: (r, ctx) => (
        <a href="#" className="text-cyan-700 font-medium hover:underline" data-open={r.id}>
          <ctx.Mark text={String(r.code == null ? '' : r.code)} tokens={ctx.tokens} />
        </a>
      ),
    },
    {
      key: '_supplierName',
      label: t('col.supplier'),
      sortable: true,
      sortValue: (r) => r._v1Rank._supplierName, // rank v1
    },
    {
      key: '_itemCount',
      label: t('col.lines'),
      sortable: true,
      sortValue: (r) => r._v1Rank._itemCount, // rank v1 (type number)
    },
    {
      key: '_total',
      label: t('col.total'),
      sortable: true,
      sortValue: (r) => r._v1Rank._total, // rank v1 (type number)
      filterText: ordTotalFilterText,
      render: (r, ctx) => <ctx.Mark text={ordTotalFilterText(r)} tokens={ctx.tokens} />,
    },
    {
      key: 'status',
      label: t('col.status'),
      sortable: true,
      sortValue: (r) => r._v1Rank.status, // rank v1 (stato RAW, non la label)
      filterText: ordStatusFilterText,
      render: (r, ctx) => (
        <span className={ordStatusBadgeClass(r.status) + ' text-xs px-2 py-0.5 rounded'}>
          <ctx.Mark text={ordStatusLabel(r.status)} tokens={ctx.tokens} />
        </span>
      ),
    },
    {
      key: 'createdAt',
      label: t('col.created'),
      sortable: true,
      sortValue: (r) => r._v1Rank.createdAt, // rank v1 (type date)
      filterText: ordCreatedFilterText,
      render: (r, ctx) => <ctx.Mark text={fmtCreated(r.createdAt)} tokens={ctx.tokens} />,
    },
    {
      key: 'expectedDate',
      label: ORD_TEXTS.expectedCol,
      sortable: true,
      sortValue: (r) => r._v1Rank.expectedDate, // rank v1 (type date)
      filterText: ordExpectedFilterText,
      render: (r, ctx) => <ctx.Mark text={r.expectedDate ? fmtCreated(r.expectedDate) : '—'} tokens={ctx.tokens} />,
    },
  ];

  return (
    <div>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold">{t('ord.title')}</h1>
          <p className="text-sm text-slate-500 mt-0.5">{ORD_TEXTS.subtitle}</p>
        </div>
        <button
          id="btn-new-order"
          type="button"
          className="bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 rounded text-sm"
          onClick={() => openNewOrder(win)}
        >{ORD_TEXTS.newBtn}</button>
      </div>
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3" id="orders-table-mount">
        <DataTable
          columns={columns}
          rows={data.rows}
          rowKey="id"
          sort={{ key: 'createdAt', dir: 'desc' }}
          filterable
          selectable
          selection={selection}
          resolveSelection={(ids) => resolveOrderSelection(win, ids)}
          bulkActions={buildOrderBulkActions(win)}
          onBulkDone={() => { selection.clear(); force(); }}
          onRowClick={(row) => openOrderDetail(win, row.id)}
          emptyMessage={ORD_TEXTS.emptyMessage}
        />
      </div>
    </div>
  );
}

// Factory (pattern createProcV3Products): il register/install la aggancia al
// WRAP di window.renderOrders. render() idempotente sul container; unmount()
// libera il container (il monolite resta vivo per le altre viste).
export function createProcV3Orders(win) {
  let root = null;
  let mountedEl = null;
  let tick = 0;
  // selezione a livello di PAGINA (deviazione dichiarata: il v1 la azzera a
  // ogni renderOrders; qui sopravvive ai refresh, pruning sugli id morti)
  const selection = new Set();

  function render() {
    const container = win.document.getElementById('orders-content');
    if (!container) throw new Error('#orders-content assente');
    if (!root || mountedEl !== container || !container.isConnected) {
      if (root) { try { root.unmount(); } catch (e) { /* già smontato */ } }
      container.replaceChildren(); // eventuali resti del v1 (catch-up)
      root = win.ReactDOM.createRoot(container);
      mountedEl = container;
    }
    tick += 1; // ogni chiamata del wrapper = refresh dati (rerenderAll, bulk, rientro)
    root.render(<OrdersList win={win} tick={tick} selection={selection} />);
  }

  function unmount() {
    if (root) {
      try { root.unmount(); } catch (e) { /* già smontato */ }
      root = null;
    }
    mountedEl = null;
    selection.clear(); // uscendo dalla vista la selezione si azzera (mai peggiore del v1)
  }

  function isMounted() { return !!root; }

  return { render, unmount, isMounted };
}
